import { getTimeAppliedAgo } from "@/helpers/getTimeAppliedAgo";
import {
  BriefCase,
  JobAge,
  Location,
  SuccessFilled,
} from "@monsterindia/global-ui-components";
import React, { useCallback } from "react";

import { generateJDUrl } from "@/components/dashboard/utils/generateJDUrl";
import { getExperienceRange } from "@/components/dashboard/utils/getExperienceRange";
import { getLocationData } from "@/components/dashboard/utils/getLocationData";
import {
  currencySymbolMap,
  getSalaryDetails,
} from "@/components/dashboard/utils/getSalaryDetails";

import JobDescHeader from "./jobDescHeader";

interface JobDetailsProps {
  data: any;
  isMobile: boolean;
  siteProps?: any;
  mediaUrl: string;
}

const JobDetails: React.FC<JobDetailsProps> = ({
  data,
  isMobile,
  siteProps,
  mediaUrl,
}) => {
  const {
    minimumSalary,
    maximumSalary,
    locations,
    minimumExperience,
    maximumExperience,
    currencyCode,
    jobSalaryConfidential,
    hideCompanyName,
    companyName,
    employmentTypes,
    title,
  } = data?.jobDetails ?? {};

  const salaryComponentCheck =
    maximumSalary?.absoluteValue &&
    minimumSalary?.absoluteValue &&
    maximumSalary?.absoluteMonthlyValue &&
    minimumSalary?.absoluteMonthlyValue;
  const timeAgo = useCallback(getTimeAppliedAgo, [data]);
  const salaryDisplay = useCallback(getSalaryDetails, [
    data.jobDetails?.minimumSalary,
    data.jobDetails?.maximumSalary,
  ]);
  const locationDisplay = useCallback(getLocationData, [
    data?.jobDetails?.locations,
  ]);
  const experienceDisplay = useCallback(getExperienceRange, [
    data.jobDetails?.minimumExperience,
    data.jobDetails?.maximumExperience,
  ]);

  const handleScroll = () => {
    const targetElement = document.getElementById("similar-jobs");
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {!isMobile ? (
        <JobDescHeader data={data} isMobile={isMobile} mediaUrl={mediaUrl} />
      ) : null}
      <div className="flex flex-col gap-[6px] py-4 md:px-6">
        <div className="mt-[17px] flex items-center justify-between md:!mt-2">
          <a
            href={`${generateJDUrl(data?.jobDetails || {})}`}
            className="decoration-skip-ink-none text-content-primary text-lg font-bold leading-[26px] decoration-solid decoration-auto underline-offset-auto [text-underline-offset:auto] [text-underline-position:from-font] hover:underline md:text-2xl md:leading-9"
          >
            {title || ""}
          </a>
        </div>
        <div className="text-content-secondary md:!text-content-primary -mt-1 line-clamp-2 text-base font-normal leading-6 md:!text-lg md:!leading-[24px]">
          <div className="line-clamp-2 text-sm font-normal md:!text-base">
            {hideCompanyName || !companyName
              ? "Company name confidential"
              : companyName || ""}
          </div>
        </div>
        <div className="mt-[6px] flex flex-col gap-2 md:!mt-0 md:flex-row md:items-center">
          <div
            className={`bg-starGrass-100 text-decorative-content-06 flex w-fit items-center gap-1 rounded-[4px] px-2 py-1 md:w-auto`}
          >
            <SuccessFilled
              width={16}
              height={16}
              fill="#2BA682"
              className="text-gumdropGreen-500"
            />
            <label className="text-xs">{`${data?.applicationData?.statusName || "Application Sent"} ${data?.applicationData?.statusName !== "No longer in consideration" ? (data?.applicationData?.statusUpdatedAt ? timeAgo(data.applicationData.statusUpdatedAt) : data?.applicationData?.appliedAt ? timeAgo(data.applicationData.appliedAt) : "") : ""}`}</label>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:!mt-2 md:!gap-4 md:!px-6">
        <div className="text-content-primary flex gap-2 text-sm font-normal md:!max-h-5">
          <div className="flex items-center gap-2">
            <span className="mt-[2px] self-start">
              <BriefCase width={16} height={16} fill="#59566C" />
            </span>
            <span>
              {experienceDisplay(
                minimumExperience?.years || undefined,
                maximumExperience?.years || undefined,
              )}
            </span>
          </div>
          {!isMobile && salaryComponentCheck ? (
            <div className="flex items-center gap-2">
              <span className="text-[#E8E8EB] md:!text-[#777584]">
                {isMobile ? "|" : "•"}
              </span>
              <div>
                {jobSalaryConfidential ? (
                  <span>Salary confidential</span>
                ) : (
                  <>
                    <span className="mr-[2px] h-[16px] w-[16px] self-start text-center text-[#59566C]">
                      {currencySymbolMap[currencyCode || "INR"]}
                    </span>
                    <span>
                      {salaryDisplay(
                        minimumSalary || "",
                        maximumSalary || "",
                        siteProps?.site_map,
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {Array.isArray(employmentTypes) && employmentTypes?.[0] && (
            <div className="flex items-center gap-2">
              <span className="text-[#777584]">•</span>
              <span>{employmentTypes[0] || ""}</span>
            </div>
          )}
        </div>

        {isMobile && salaryComponentCheck ? (
          <div className="text-content-primary flex items-center gap-2 text-sm font-normal">
            {jobSalaryConfidential ? (
              <span>Salary confidential</span>
            ) : (
              <>
                <span className="h-[16px] w-[16px] self-start text-center text-[#59566C]">
                  {currencySymbolMap[data.jobDetails?.currencyCode || "INR"]}
                </span>
                <span>
                  {salaryDisplay(
                    minimumSalary || "",
                    maximumSalary || "",
                    siteProps?.site_map,
                  )}
                </span>
              </>
            )}
          </div>
        ) : null}

        <div className="text-content-primary flex gap-2 text-sm font-normal">
          <span className="mt-[2px] self-start">
            <Location width={16} height={16} fill="#59566C" />
          </span>
          {data.jobDetails?.locations.length > 0 ? (
            <span>
              {locations && locationDisplay(data.jobDetails?.locations)}
            </span>
          ) : (
            ""
          )}
        </div>

        <div className="text-content-primary flex gap-2 text-sm font-normal">
          <span className="mt-[2.5px] self-start">
            <JobAge width={16} height={16} fill="#59566C" />
          </span>
          <span>
            {(data?.applicationData?.appliedAt &&
              `Applied ${timeAgo(data.applicationData?.appliedAt)}`) ||
              ""}
          </span>
        </div>

        {!isMobile && (
          <div className="flex gap-2 text-[14px] font-semibold leading-[20px] text-[#6E00BE] md:py-1">
            <button onClick={handleScroll} className="cursor-pointer">
              Similar Jobs
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(JobDetails);
